/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { GenericDialogComponent } from 'src/app/common/generic-dialog/generic-dialog.component';
import { subjectSubscribeWithoutComplete } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { IHoverOverUserPreferences } from 'src/app/services/hover-over-preferences.service';
import {
	ChecklistDataMap,
	IChecklistItemDataWithId,
} from 'src/app/shared/ui/material/checkbox-list/checkbox-list-model';
import { IHoverOverContentOption } from '../hover-over-content-configuration-list';

@Component({
	selector: 'hover-over-content-editor',
	templateUrl: './hover-over-content-editor.component.html',
	styleUrls: ['./hover-over-content-editor.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HoverOverContentEditorComponent extends GenericDialogComponent implements OnInit, OnDestroy {
	public dataSubject$ = new BehaviorSubject<
		[IHoverOverContentOption[]?, Partial<IHoverOverUserPreferences>?, IChecklistItemDataWithId[]?]
	>([]);

	public checkboxListData$ = this.dataSubject$.pipe(
		filter(([hoverOverData]: [IHoverOverContentOption[]]) => !!hoverOverData),
		map(([hoverOverData]: [IHoverOverContentOption[]]) =>
			hoverOverData.filter((option) => option.id !== 'allCustomFields')
		),
		takeUntil(this.destroy$)
	);

	/**
	 * Used exclusively as a workaround for TP-5819 by the checkbox set
	 * [ids]="checkboxListCustomAllId$ | async"
	 * The more correct fix would be moving the showAllcustomFields data fully
	 * out of tooltipContentForm.tooltipContent. But a bunch of stuff relies on it.
	 * Consider improving if changing things in here
	 */
	protected checkboxListCustomAllId$ = this.checkboxListData$.pipe(
		map((list) => {
			if (!list) {
				return [];
			}
			return list.map((item) => item.id);
		})
	);

	public customFieldsList$ = this.dataSubject$.pipe(
		map(([hoverOverData, settings, customFields]) => customFields)
	);

	public tooltipContentForm = new FormGroup({
		tooltipContent: new FormControl<ChecklistDataMap>(new Map()),
		customFieldContent: new FormControl<ChecklistDataMap>(new Map()),
		showAllcustomFields: new FormControl<boolean>(true),
	});

	get showAllcustomFields() {
		return this.tooltipContentForm.get('showAllcustomFields') as FormControl<boolean>;
	}

	public ngOnInit() {
		//ugh, fixing this type requires a new inheritance generics... too much scope
		this.data.dataObservable.subscribe(subjectSubscribeWithoutComplete(this.dataSubject$ as any));

		this.dataSubject$
			.pipe(
				filter(
					([hoverOverData, settings]: [IHoverOverContentOption[], Partial<IHoverOverUserPreferences>]) =>
						!!hoverOverData || !!settings
				),
				map(([hoverOverData, settings]: [IHoverOverContentOption[], Partial<IHoverOverUserPreferences>]) => {
					// Map data for tooltipContent FormControl
					const settingsMap: ChecklistDataMap = new Map();

					hoverOverData.forEach((option) => {
						settingsMap.set(option.id, {
							isChecked: settings[option.propertyName],
							originalModel: option,
						});
					});

					return settingsMap;
				}),
				takeUntil(this.destroy$)
			)
			.subscribe((settingsMap) => {
				this.tooltipContentForm.get('tooltipContent').setValue(settingsMap, { emitValue: false });
				this.showAllcustomFields.setValue(settingsMap.get('allCustomFields').isChecked);
			});

		this.customFieldContentFormInit();

		this.showAllcustomFields.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((showAllcustomFields: boolean) => {
				this.setCustomFieldListDisabled(showAllcustomFields);
			});
	}

	public onButtonSaveSelect() {
		const tooltipContentFormValue = this.tooltipContentForm.get('tooltipContent').value;
		tooltipContentFormValue.set('allCustomFields', {
			isChecked: this.showAllcustomFields.value,
		});

		this.dialogRef.close({
			staticHoverPreferences: this.tooltipContentForm.get('tooltipContent').value,
			customFieldsPreferences: this.tooltipContentForm.get('customFieldContent').value,
		});
	}

	public onButtonCloseSelect() {
		this.dialogRef.close();
	}

	private setCustomFieldListDisabled(isDisabled: boolean) {
		if (isDisabled) {
			this.tooltipContentForm.get('customFieldContent').disable({ emitEvent: false });
		} else {
			this.tooltipContentForm.get('customFieldContent').enable({ emitEvent: false });
		}
	}

	private customFieldContentFormInit() {
		this.dataSubject$
			.pipe(
				map(
					([hoverOverData, settings, customFields]: [
						IHoverOverContentOption[],
						Partial<IHoverOverUserPreferences>,
						IChecklistItemDataWithId[]
					]) => customFields
				),
				filter((customFields) => !!customFields),
				map((customFields) => {
					const customFieldsMap: ChecklistDataMap = new Map();

					customFields.forEach((option) => {
						customFieldsMap.set(option.id, {
							isChecked: option.isChecked,
						});
					});

					return customFieldsMap;
				}),
				takeUntil(this.destroy$)
			)
			.subscribe((customFieldsMap) =>
				this.tooltipContentForm.get('customFieldContent').setValue(customFieldsMap, { emitValue: false })
			);
	}
}
